import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Instagram, Send, Loader2, ArrowUpRight } from "lucide-react";
import { TiktokIcon } from "./icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { CONTACT, SERVICE_OPTIONS, waLink } from "../data/content";
import { fadeUp, staggerContainer, viewportOnce, EYEBROW } from "../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: "", phone: "", email: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Mohon lengkapi nama, nomor telepon, dan pesan Anda.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, form);
      toast.success("Pesan terkirim! Anda akan diarahkan ke WhatsApp kami.");
      const waMsg = `Halo FYLegal.ID, saya ${form.name}.%0ATelepon: ${form.phone}%0AEmail: ${
        form.email || "-"
      }%0ALayanan: ${form.service || "-"}%0APesan: ${form.message}`;
      window.open(waLink(decodeURIComponent(waMsg)), "_blank");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Gagal mengirim. Silakan coba lagi atau hubungi via WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: Phone, label: "Telepon / WhatsApp", value: CONTACT.phoneDisplay, href: waLink() },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: MapPin, label: "Alamat", value: CONTACT.address, href: null },
    { icon: Instagram, label: "Instagram", value: CONTACT.instagramHandle, href: CONTACT.instagram },
    { icon: TiktokIcon, label: "TikTok", value: CONTACT.tiktokHandle, href: CONTACT.tiktok },
  ];

  const inputCls =
    "w-full rounded-2xl bg-white/5 border border-white/15 px-5 py-3.5 text-white placeholder:text-white/40 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all";

  return (
    <section id="kontak" data-testid="contact-section" className="relative bg-navy-dark py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 noise opacity-[0.05]" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="max-w-3xl"
        >
          <span className={EYEBROW}>Kontak</span>
          <h2 className="mt-5 font-serif text-4xl md:text-6xl tracking-tight text-white leading-[1.02]">
            Konsultasikan Kebutuhan <span className="italic text-gold">Hukum Anda</span>
          </h2>
          <p className="mt-5 text-white/60 text-lg">
            Tim FYLegal.ID siap menjadi partner hukum Anda — mulai dari legalitas usaha hingga
            pendampingan penyelesaian sengketa.
          </p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <motion.form
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            onSubmit={handleSubmit}
            data-testid="consultation-form"
            className="lg:col-span-7 rounded-3xl bg-navy-light/50 border border-white/10 p-7 md:p-10 backdrop-blur-sm"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/70 mb-2 block">Nama Lengkap *</label>
                <input
                  data-testid="form-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Nama Anda"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-2 block">Nomor Telepon *</label>
                <input
                  data-testid="form-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="08xxxxxxxxxx"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-2 block">Email</label>
                <input
                  data-testid="form-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="email@contoh.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-2 block">Layanan</label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger
                    data-testid="form-service"
                    className="w-full rounded-2xl bg-white/5 border-white/15 px-5 py-6 text-white data-[placeholder]:text-white/40 focus:ring-gold"
                  >
                    <SelectValue placeholder="Pilih layanan" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-sm text-white/70 mb-2 block">Pesan / Kebutuhan Anda *</label>
              <textarea
                data-testid="form-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={4}
                placeholder="Ceritakan kebutuhan hukum Anda..."
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="form-submit"
              className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-gold text-navy px-8 py-4 font-semibold hover:bg-gold-light transition-all duration-300 shadow-gold disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              {loading ? "Mengirim..." : "Kirim & Lanjut ke WhatsApp"}
            </button>
          </motion.form>

          {/* Info */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="lg:col-span-5 flex flex-col gap-4"
          >
            {info.map((it) => {
              const Icon = it.icon;
              const content = (
                <div className="flex items-start gap-4 rounded-3xl bg-navy-light/50 border border-white/10 p-6 hover:border-gold/40 transition-colors h-full">
                  <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-gold/15 text-gold shrink-0">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-white/50">{it.label}</p>
                    <p className="mt-1 text-white font-medium leading-snug">{it.value}</p>
                  </div>
                </div>
              );
              return it.href ? (
                <motion.a
                  key={it.label}
                  variants={fadeUp}
                  href={it.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`contact-info-${it.label}`}
                >
                  {content}
                </motion.a>
              ) : (
                <motion.div key={it.label} variants={fadeUp} data-testid={`contact-info-${it.label}`}>
                  {content}
                </motion.div>
              );
            })}
            <motion.a
              variants={fadeUp}
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-wa-direct"
              className="mt-auto inline-flex items-center justify-between gap-2 rounded-3xl bg-gold text-navy px-7 py-5 font-semibold hover:bg-gold-light transition-colors"
            >
              Chat Langsung via WhatsApp
              <ArrowUpRight className="h-5 w-5" />
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
